import React, { useEffect, useMemo, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  ModalProvider,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'

import type { MenuResponse } from '@library/api-menu-experience'

import Loading from 'routes/Menu/Loading'
import { JustForYouTutorial } from 'routes/Menu/components/JustForYouTutorial'
import { MenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/MenuPreferencesTutorial'
import { useShouldShowMenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/useShouldShowMenuPreferencesTutorial'
import { useBasket } from 'routes/Menu/domains/basket'
import { CollectionSlug } from 'routes/Menu/domains/collections'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'
import { MenuExperienceCategoryNav } from './MenuExperienceCategoryNav'
import { MenuExperienceRecipeDetailOverlay } from './MenuExperienceRecipeDetailOverlay'
import { MenuExperienceSubHeader } from './MenuExperienceSubHeader'
import { useMenuExperienceBasketRecipeTileSync } from './hooks/useMenuExperienceBasketRecipeTileSync'
import { useMenuExperienceNavigation } from './hooks/useMenuExperienceNavigation'
import { MenuBrandingContext, MenuBrandingProvider, getMenuBranding } from './menuBrandingContext'
import { MenuExperienceNavigationProvider } from './menuExperienceNavigationContext'
import { MenuExperienceRecipeDetailProvider } from './menuExperienceRecipeDetailContext'
import { RecipeGridCustomisationProvider } from './recipeGridCustomisationContext'

type MenuExperiencePageProps = {
  location: {
    pathname?: string
    query: MenuQueryParams & Record<string, unknown> & { collection?: string }
    [key: string]: unknown
  }
  [key: string]: unknown
}

const ContentContainer = withCitrusTheme(Box, (theme) => ({
  margin: '1rem auto 0',
  padding: '0 0 1rem',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    padding: '0 30px 1rem',
  },
}))

const MenuExperienceBasketRecipeTileSynchronizer = ({
  menuContent,
  query,
}: {
  menuContent: MenuResponse['content']
  query: MenuExperiencePageProps['location']['query']
}) => {
  useMenuExperienceBasketRecipeTileSync(query, menuContent)

  return null
}

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const hasMountedRef = useRef(false)
  const lastLoadedMenuContextRef = useRef<Pick<MenuResponse, 'categoryId' | 'menuId'>>()
  const { data, error, links, selectCategory } = useMenuExperienceNavigation(
    location.query,
    location.pathname,
  )
  const { updateRecipePortionsByUuid } = useBasket()
  const navigationActions = useMemo(() => ({ selectCategory }), [selectCategory])
  const isCategoryNavVisible = links.some((link) => link.selected)
  const recommendationsCategory = data
    ? data.categories.find(({ slug }) => slug === CollectionSlug.Recommendations)
    : undefined
  const recommendationsCollection = data
    ? recommendationsCategory
      ? { shortTitle: recommendationsCategory.title }
      : null
    : undefined
  const showMenuPreferencesTutorial =
    useShouldShowMenuPreferencesTutorial(recommendationsCollection)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.query.collection])

  useEffect(() => {
    if (data) {
      lastLoadedMenuContextRef.current = {
        categoryId: data.categoryId,
        menuId: data.menuId,
      }
    }
  }, [data])

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    const loading = (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
    const lastLoadedMenuContext = lastLoadedMenuContextRef.current

    return lastLoadedMenuContext ? (
      <RecipeGridCustomisationProvider
        key={lastLoadedMenuContext.menuId}
        collectionId={lastLoadedMenuContext.categoryId}
        menuId={lastLoadedMenuContext.menuId}
      >
        {loading}
      </RecipeGridCustomisationProvider>
    ) : (
      loading
    )
  }

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper
  const menuBranding = getMenuBranding(data.content)
  const shouldGoBackToBrowse = data.type !== 'browse' && !isCategoryNavVisible

  const content = (
    <Box data-testid="menu-experience-page">
      <MenuExperienceBasketRecipeTileSynchronizer
        menuContent={data.content}
        query={location.query}
      />
      <MenuExperienceSubHeader
        pathname={location.pathname}
        query={location.query}
        shouldGoBackToBrowse={shouldGoBackToBrowse}
      />
      <JustForYouTutorial recommendationsCollection={recommendationsCollection} />
      {showMenuPreferencesTutorial && <MenuPreferencesTutorial />}
      {isCategoryNavVisible && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={{ position: 'sticky', top: 0 }}>
          <MenuExperienceCategoryNav
            links={links}
            pathname={location.pathname}
            query={location.query}
          />
        </Box>
      )}
      <MenuBrandingProvider value={menuBranding}>
        <MenuExperienceRecipeDetailProvider
          menuId={data.menuId}
          updateRecipePortions={updateRecipePortionsByUuid}
        >
          <MenuExperienceNavigationProvider value={navigationActions}>
            <MenuBrandingContext.Consumer>
              {({ backgroundColor, backgroundImageUrl, textColor }) => (
                <ContentContainer
                  data-testid="menu-experience-page-content"
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  maxWidth="1600px"
                  style={{
                    marginTop: backgroundColor || backgroundImageUrl ? 0 : undefined,
                    ...(backgroundColor ? { backgroundColor } : {}),
                    ...(textColor ? { color: textColor } : {}),
                    ...(backgroundImageUrl
                      ? {
                          backgroundImage: `url(${backgroundImageUrl})`,
                          backgroundPosition: 'top center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '100% auto',
                        }
                      : {}),
                  }}
                >
                  <ModalProvider>
                    <ScreenWrapper contentBlocks={data.content} collectionId={data.categoryId} />
                  </ModalProvider>
                </ContentContainer>
              )}
            </MenuBrandingContext.Consumer>
          </MenuExperienceNavigationProvider>
          <MenuExperienceRecipeDetailOverlay collectionId={data.categoryId} />
        </MenuExperienceRecipeDetailProvider>
      </MenuBrandingProvider>
    </Box>
  )

  return (
    <RecipeGridCustomisationProvider
      key={data.menuId}
      collectionId={data.categoryId}
      menuId={data.menuId}
    >
      {content}
    </RecipeGridCustomisationProvider>
  )
}
